import { injectable, symbolToken } from '@servicetitan/react-ioc';
import { ApiModels } from '../../api';
import { loadPendoScript } from './load-pendo';

const ENV_ID = process.env.ENV_ID;
export const PENDO_API_TOKEN = symbolToken<IPendoApi>('PENDO_API_TOKEN');

export const PendoEvents = {
    CaseSearch: 'ish-case-search',
    CaseSearchSuccess: 'ish-case-search-success',
    CaseSearchError: 'ish-case-search-error',
    CaseSearchResultNavigate: 'ish-case-search-result-navigate',
    CaseExternalLinkNavigate: 'ish-case-external-link-navigate',
    CaseMediaItemTooltipViewed: 'ish-case-media-item-tooltip-viewed',
    CaseLoaded: 'ish-case-loaded',
    CaseMediaLoaded: 'ish-case-media-loaded',
    CaseImportantInfoLoaded: 'ish-case-important-info-loaded',
    CaseSummaryLoaded: 'ish-case-summary-loaded',
    ChatbotAskQuestion: 'ish-chatbot-ask-question',
    CaseFullStoryLoaded: 'ish-case-full-story-loaded',
    CaseRecentsLoaded: 'ish-case-recents-loaded',
    SimilarJiraIssuesLoaded: 'ish-similar-jira-issues-loaded',
    CaseProProductsLoaded: 'ish-case-pro-products-loaded',
    CaseMediaDownloadStandardView: 'ish-case-media-download-standard-view',
    CaseMediaDownloadZoomedView: 'ish-case-media-download-zoomed-view',
} as const;
export type PendoEvents = (typeof PendoEvents)[keyof typeof PendoEvents];

interface PendoEventProperties {
    [PendoEvents.CaseSearch]: {};
    [PendoEvents.CaseSearchSuccess]: { hasResults: boolean };
    [PendoEvents.CaseSearchError]: {};
    [PendoEvents.CaseSearchResultNavigate]: { caseNumber?: string };
    [PendoEvents.CaseExternalLinkNavigate]: { hasAccess: boolean; utmSource: string };
    [PendoEvents.CaseMediaItemTooltipViewed]: {};
    // Tracking case loading events
    [PendoEvents.CaseLoaded]: { success: boolean; isOwner: boolean };
    [PendoEvents.CaseMediaLoaded]: { success: boolean };
    [PendoEvents.CaseImportantInfoLoaded]: { success: boolean };
    [PendoEvents.CaseSummaryLoaded]: { success: boolean };
    [PendoEvents.CaseFullStoryLoaded]: { success: boolean };
    [PendoEvents.CaseRecentsLoaded]: { success: boolean };
    // Tracking chatbot events
    [PendoEvents.ChatbotAskQuestion]: {};
    [PendoEvents.SimilarJiraIssuesLoaded]: { success: boolean; count: number };
    [PendoEvents.CaseProProductsLoaded]: { success: boolean };
    [PendoEvents.CaseMediaDownloadStandardView]: { mediaType: 'image' | 'video' | 'attachment' };
    [PendoEvents.CaseMediaDownloadZoomedView]: { mediaType: 'image' | 'video' };
}

export interface IPendoApi {
    initialize(user?: ApiModels.User): Promise<void>;
    trackEvent<T extends PendoEvents>(eventName: T, properties?: PendoEventProperties[T]): void;
    flushEvents(): void;
}

@injectable()
export class PendoApi implements IPendoApi {
    private loadingPromise?: Promise<void>;

    private get pendo() {
        return (window as any)?.pendo;
    }

    async initialize(user?: ApiModels.User) {
        if (ENV_ID === 'cypress' || typeof window === 'undefined') {
            // eslint-disable-next-line no-console
            console.log('Pendo disabled in cypress env');
            return;
        }
        this.loadingPromise ??= loadPendoScript().then(() => {
            this.pendo?.initialize({
                visitor: {
                    id: user?.visitorId,
                    name: user?.userDisplayName,
                    username: user?.userName,
                    email: user?.email,
                },
                account: {
                    id: 'IntelligentSupportHub',
                },
            });
        });
        await this.loadingPromise;
    }

    trackEvent<T extends PendoEvents>(eventName: T, properties?: PendoEventProperties[T]): void {
        if (!this.pendo) {
            // eslint-disable-next-line no-console
            console.warn('[PendoApi] trackEvent: Pendo is not initialized');
        }
        this.pendo?.track(eventName, properties);
    }

    flushEvents() {
        if (!this.pendo) {
            // eslint-disable-next-line no-console
            console.warn('[PendoApi] flushEvents: Pendo is not initialized');
        }
        this.pendo?.flushNow?.(true);
    }
}
